import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import AnticheatDebugger from '../components/anticheatDebugger';
import * as actions from '../redux/actions/anticheatDebuggerActions';
import { useAdminWebSocketStore } from '../stores/websocketStore';

const AnticheatDebuggerPageContainer = ({
  debuggerState,
  actions,
  userId,
}) => {
  const { subscribe, subscribeMatchDebug, unsubscribeMatchDebug } = useAdminWebSocketStore();
  const [activeMatches, setActiveMatches] = useState([]);
  const selectedMatchIdRef = useRef(null);
  const userIdRef = useRef(userId);

  useEffect(() => {
    selectedMatchIdRef.current = debuggerState.selectedMatchId;
  }, [debuggerState.selectedMatchId]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    actions.fetchActiveMatches({ userId }).then(setActiveMatches);
  }, [userId]);

  // Unmount-only cleanup: actively disables instrumentation so the plugin stops
  // streaming (reading pawns / uploading positions) instead of running on until
  // the Redis TTL expires with nobody watching. Uses refs (rather than
  // debuggerState.selectedMatchId in the deps) so this effect registers once and
  // only runs its cleanup on unmount, not on every match switch (onSelectMatch
  // already tears down the prior match on switch).
  useEffect(() => {
    return () => {
      const matchId = selectedMatchIdRef.current;
      if (matchId) {
        actions.setInstrumentation({ matchId, userId: userIdRef.current, enabled: false });
        unsubscribeMatchDebug(matchId);
        actions.resetDebugger();
      }
    };
  }, []);

  useEffect(() => {
    const unsub = subscribe(({ message }) => {
      if (!message?.messageId) return;
      if (message.matchId && message.matchId !== debuggerState.selectedMatchId) return;

      switch (message.messageId) {
        case 'match-debug-frame':
          actions.applyFrame(message);
          break;
        case 'match-debug-event':
          if (message.event) actions.applyEvent(message.event);
          break;
        case 'match-debug-status':
          actions.setStatus(message.streaming ? 'streaming' : 'idle');
          break;
        default:
          break;
      }
    });

    return unsub;
  }, [subscribe, debuggerState.selectedMatchId]);

  const onSelectMatch = useCallback(
    (matchId) => {
      const prev = debuggerState.selectedMatchId;
      if (prev && prev !== matchId) {
        // Stop the previously selected match's stream before moving on, so we
        // never leave a second match's Redis flag set / plugin streaming.
        actions.setInstrumentation({ matchId: prev, userId, enabled: false });
        unsubscribeMatchDebug(prev);
      }
      actions.selectMatch(matchId);
      subscribeMatchDebug(matchId);
    },
    [actions, debuggerState.selectedMatchId, userId, subscribeMatchDebug, unsubscribeMatchDebug]
  );

  const onToggle = useCallback(
    (enabled) => {
      if (!debuggerState.selectedMatchId) return;
      actions.setInstrumentation({ matchId: debuggerState.selectedMatchId, userId, enabled });
    },
    [actions, debuggerState.selectedMatchId, userId]
  );

  return (
    <AnticheatDebugger
      state={debuggerState}
      activeMatches={activeMatches}
      onSelectMatch={onSelectMatch}
      onToggle={onToggle}
    />
  );
};

const mapStateToProps = (state) => ({
  debuggerState: state.anticheatDebugger,
  userId: state.catalogReducer?.user?.userId,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AnticheatDebuggerPageContainer);
